/**
 * Unified error handling utility
 * Provides consistent error handling across the application
 */

import { message } from 'antd';

export interface ApiError {
  status?: number;
  message?: string;
  detail?: string;
  errors?: any;
}

/**
 * Extract error message from various error formats
 */
export const getErrorMessage = (error: any): string => {
  // Axios error with response
  if (error.response) {
    const { status, data } = error.response;
    
    // Pydantic validation error
    if (data?.detail && Array.isArray(data.detail)) {
      const messages = data.detail.map((err: any) => {
        const field = err.loc?.join('.') || 'unknown';
        return `${field}: ${err.msg}`;
      });
      return messages.join('; ');
    }
    
    // Standard error message
    if (data?.detail) {
      return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    }
    
    if (data?.message) {
      return data.message;
    }
    
    // HTTP status code messages
    return getStatusMessage(status);
  }
  
  // Network error
  if (error.request) {
    return '网络连接失败，请检查网络设置';
  }
  
  // Other errors
  if (error.message) {
    return error.message;
  }
  
  return '未知错误，请稍后重试';
};

/**
 * Get user-friendly message for HTTP status codes
 */
export const getStatusMessage = (status: number): string => {
  const statusMessages: Record<number, string> = {
    400: '请求参数错误',
    401: '未登录或登录已过期，请重新登录',
    403: '无权限访问此资源',
    404: '请求的资源不存在',
    409: '资源冲突，请刷新后重试',
    422: '数据验证失败',
    429: '请求过于频繁，请稍后再试',
    500: '服务器内部错误',
    502: '网关错误',
    503: '服务暂时不可用',
    504: '网关超时',
  };
  
  return statusMessages[status] || `请求失败 (${status})`;
};

/**
 * Handle API error with appropriate user feedback
 */
export const handleApiError = (error: any, customMessages?: Partial<Record<number, string>>): void => {
  const status = error.response?.status;
  
  // Check for custom message
  if (status && customMessages && customMessages[status]) {
    message.error(customMessages[status]);
    return;
  }
  
  // Get error message
  const errorMessage = getErrorMessage(error);
  
  // Show error message
  if (status === 401) {
    message.error(errorMessage);
    // Redirect to login after a short delay
    setTimeout(() => {
      window.location.href = '/login';
    }, 1500);
  } else {
    message.error(errorMessage);
  }
};

/**
 * Handle validation errors from backend
 */
export const handleValidationError = (error: any): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};
  
  if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    error.response.data.detail.forEach((err: any) => {
      const field = err.loc?.[err.loc.length - 1] || 'unknown';
      fieldErrors[field] = err.msg;
    });
  }
  
  return fieldErrors;
};

/**
 * Show success message
 */
export const showSuccess = (msg: string = '操作成功'): void => {
  message.success(msg);
};

/**
 * Show error message
 */
export const showError = (msg: string = '操作失败'): void => {
  message.error(msg);
};

/**
 * Show warning message
 */
export const showWarning = (msg: string): void => {
  message.warning(msg);
};

/**
 * Show info message
 */
export const showInfo = (msg: string): void => {
  message.info(msg);
};

/**
 * Error handler for async operations
 * Usage: await asyncOperation().catch(asyncErrorHandler('操作失败'))
 */
export const asyncErrorHandler = (defaultMessage?: string) => (error: any) => {
  handleApiError(error);
  if (defaultMessage) {
    console.error(defaultMessage, error);
  }
  throw error; // Re-throw to allow caller to handle if needed
};

/**
 * Wrap async function with error handling
 */
export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  errorMessage?: string
): T => {
  return (async (...args: any[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      handleApiError(error);
      if (errorMessage) {
        console.error(errorMessage, error);
      }
      throw error;
    }
  }) as T;
};

/**
 * Check if error is a specific HTTP status
 */
export const isErrorStatus = (error: any, status: number): boolean => {
  return error.response?.status === status;
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: any): boolean => {
  return isErrorStatus(error, 422) || isErrorStatus(error, 400);
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: any): boolean => {
  return isErrorStatus(error, 401);
};

/**
 * Check if error is a permission error
 */
export const isPermissionError = (error: any): boolean => {
  return isErrorStatus(error, 403);
};

/**
 * Check if error is a not found error
 */
export const isNotFoundError = (error: any): boolean => {
  return isErrorStatus(error, 404);
};

/**
 * Format validation errors for Ant Design Form
 */
export const formatFormErrors = (error: any): Array<{ name: string; errors: string[] }> => {
  const formErrors: Array<{ name: string; errors: string[] }> = [];
  
  if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    error.response.data.detail.forEach((err: any) => {
      const fieldName = err.loc?.[err.loc.length - 1];
      if (fieldName) {
        formErrors.push({
          name: fieldName,
          errors: [err.msg],
        });
      }
    });
  }
  
  return formErrors;
};

export default {
  getErrorMessage,
  getStatusMessage,
  handleApiError,
  handleValidationError,
  showSuccess,
  showError,
  showWarning,
  showInfo,
  asyncErrorHandler,
  withErrorHandling,
  isErrorStatus,
  isValidationError,
  isAuthError,
  isPermissionError,
  isNotFoundError,
  formatFormErrors,
};
